import { useState, useEffect } from 'react';
import { X, Search, ArrowUpDown, ChevronDown, Edit2, Save, Plus, ChevronRight } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { updateTemplate } from '../../store/reducers';

// Template data structure
export function getTemplates() {
  return [
    {
      id: 'almeria',
      title: 'Almeria',
      color: '#2DD0D6', // Blue
      data: {
        regimen_solicitud: '10',
        declaracion_1: 'EX',
        declaracion_2: 'A',
        naturaleza_transaccion: '11',
        telefono: '948403026',
        contacto: 'ADUANAS@GDAR.ES',
        marcas: 'RTDAS',
        medio_transporte: '30',
      },
    },
    {
      id: 'iberveg',
      title: 'IBERVEG',
      color: '#10B981', // Green
      data: {
        aduana_despacho: '0441',
        pais_exportacion: 'ES',
        provincia: 'AL',
        localizacion_mercancia: '0441LAME05',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'I5007', descripcion: 'INFORMACION ADICIONAL FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1009', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'actiture',
      title: 'ACTITURE',
      color: '#F59E0B', // Amber
      data: {
        aduana_despacho: '3161',
        aduana_salida: 'FR000740',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'zanobeet',
      title: 'ZANOBEET',
      color: '#8B5CF6', // Purple
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'CA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'cebollas',
      title: 'CEBOLLAS',
      color: '#126FEF',
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
  ];
}

export const TemplateModal = ({
  templates: templatesProp,
  isOpen,
  onClose,
  onSelect,
  selectedTemplate,
  title,
  isSpanish,
}) => {
  // Get templates from Redux if not provided as prop
  const reduxTemplates = useSelector((state) => {
    const templates = state.templates;
    // Ensure templates is always an array
    return Array.isArray(templates) ? templates : [];
  });
  const templates = (templatesProp && Array.isArray(templatesProp)) 
    ? templatesProp 
    : (Array.isArray(reduxTemplates) ? reduxTemplates : []);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedTemplateForDetails, setSelectedTemplateForDetails] = useState(null);
  const [shouldShowMainModal, setShouldShowMainModal] = useState(isOpen);
  const [fieldSelectorTemplate, setFieldSelectorTemplate] = useState(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Sync shouldShowMainModal with isOpen prop
  useEffect(() => {
    setShouldShowMainModal(isOpen);
  }, [isOpen]);

  const sortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
  ];

  const filteredTemplates = templates.filter((template) => {
    // Validate template structure
    if (!template || typeof template !== 'object') {
      return false;
    }
    
    const searchLower = searchQuery.toLowerCase();
    
    // Search in title (with safety check)
    if (template.title && typeof template.title === 'string') {
      if (template.title.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    // Search in field values
    if (template.data && typeof template.data === 'object') {
      try {
        const fieldValues = Object.values(template.data)
          .filter(val => val !== null && val !== undefined)
          .map(val => {
            if (Array.isArray(val)) {
              return val.map(item => 
                typeof item === 'object' && item !== null ? Object.values(item).join(' ') : String(item)
              ).join(' ');
            }
            return String(val);
          })
          .join(' ')
          .toLowerCase();
        
        if (fieldValues.includes(searchLower)) {
          return true;
        }
      } catch (error) {
        console.warn('Error filtering template field values:', error);
      }
    }
    return false;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    // Safety checks for template structure
    const titleA = (a && typeof a === 'object' && a.title) ? String(a.title) : '';
    const titleB = (b && typeof b === 'object' && b.title) ? String(b.title) : '';
    
    switch (sortBy) {
      case 'name-asc':
        return titleA.localeCompare(titleB);
      case 'name-desc':
        return titleB.localeCompare(titleA);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find((option) => option.value === sortBy)?.label || (isSpanish ? 'Ordenar por' : 'Sort by');
  };

  // Helper function to format field names
  const formatFieldName = (fieldName) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to organize template fields hierarchically
  const organizeTemplateFields = (template) => {
    // Validate template structure
    if (!template || typeof template !== 'object') {
      return { declarationFields: [], documentFields: [] };
    }
    
    const { data } = template;
    const declarationFields = [];
    const documentFields = [];
    
    // Validate data exists and is an object
    if (!data || typeof data !== 'object') {
      return { declarationFields, documentFields };
    }
    
    // Separate declaration fields from document fields
    // Include all fields, even if empty (to show newly added fields)
    try {
      Object.keys(data).forEach((key) => {
        if (key !== 'documentos' && data[key] !== null && data[key] !== undefined) {
          declarationFields.push({ name: key, value: data[key] });
        }
      });
    } catch (error) {
      console.warn('Error organizing declaration fields:', error);
    }

    // Organize documentos if they exist
    // Include all document fields, even if empty
    if (data.documentos && Array.isArray(data.documentos) && data.documentos.length > 0) {
      try {
        data.documentos.forEach((doc, index) => {
          // Validate doc structure
          if (!doc || typeof doc !== 'object') {
            return; // Skip invalid documents
          }
          
          const docFields = Object.keys(doc)
            .filter(key => doc[key] !== null && doc[key] !== undefined)
            .map(key => ({
              name: key,
              value: doc[key]
            }));
          if (docFields.length > 0) {
            documentFields.push({
              docIndex: index,
              fields: docFields
            });
          }
        });
      } catch (error) {
        console.warn('Error organizing document fields:', error);
      }
    }

    return { declarationFields, documentFields };
  };

  const openTemplateDetails = (template, e) => {
    e.stopPropagation(); // Prevent template selection when clicking "View details"
    setSelectedTemplateForDetails(template);
    // Close the main template modal to avoid overlay overlap
    setShouldShowMainModal(false);
    onClose();
  };

  const closeTemplateDetails = () => {
    setSelectedTemplateForDetails(null);
    // Reopen the main template modal when closing details
    setShouldShowMainModal(true);
  };

  return (
    <>
      {/* Main Template Modal */}
      {shouldShowMainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{title}</h2>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setIsSortOpen(false);
                  setShouldShowMainModal(false);
                  onClose();
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex gap-2 mb-2">
              {/* Search Input */}
              <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1 flex-row">
                <div className="flex-0 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pr-4 py-2 !border-none no-border search-input text-sm"
                  style={{ borderWidth: '0px !important', border: '0px !important' }}
                  placeholder={isSpanish ? 'Buscar plantillas...' : 'Search templates...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Custom Sort Dropdown */}
              <div className="relative button-no-hover-effect text-sm flex">
                <button
                  className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full text-sm"
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span>{getSortLabel()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </button>

                {isSortOpen && (
                  <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          sortBy === option.value ? 'text-primary' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
              {templates.length > 0 ? (
                <div className="space-y-2">
                  {sortedTemplates.length > 0 ? (
                    sortedTemplates.map((template) => {
                      // Validate template structure
                      if (!template || typeof template !== 'object' || !template.id) {
                        return null; // Skip invalid templates
                      }
                      
                      const { declarationFields, documentFields } = organizeTemplateFields(template);
                      const hasFields = declarationFields.length > 0 || documentFields.length > 0;
                      
                      return (
                        <div
                          key={template.id}
                          className={`cursor-pointer border-bottom-gray`}
                          onClick={() => {
                            if (template && typeof template === 'object') {
                              onSelect(template);
                              setSearchQuery('');
                              setIsSortOpen(false);
                              setShouldShowMainModal(false);
                              onClose();
                            }
                          }}
                        >
                          <div className={`rounded-md hover:bg-gray-100 p-2 px-2 my-2  ${
                          selectedTemplate &&
                          selectedTemplate.id === template.id
                            ? 'bg-gray-100'
                            : ''
                        }`}>
                        <div className="flex items-start gap-0">
                          <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-4 h-4 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: template.color || '#CCCCCC' }}
                                  />
                                  <p className={`uppercase font-medium ${selectedTemplate && selectedTemplate.id === template.id ? 'font-bold' : ''}`}>
                                    {template.title || 'Untitled Template'}
                                  </p>
                                </div>
                                {hasFields && (
                                  <button
                                    onClick={(e) => openTemplateDetails(template, e)}
                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-200a rounded-fulla px-0 py-1"
                                  >
                                    <span>{isSpanish ? 'Ver detalles' : 'View details'}</span>
                                    <ChevronRight className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    );
                  })
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {isSpanish
                        ? `No se encontraron plantillas que coincidan con "${searchQuery}"`
                        : `No templates found matching "${searchQuery}"`}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="opacity-75 mb-4">
                    {isSpanish ? 'No hay plantillas disponibles' : 'No Templates Available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Details Modal - Renders independently */}
      {selectedTemplateForDetails && (
        <TemplateDetailsModal
          template={selectedTemplateForDetails}
          isOpen={!!selectedTemplateForDetails}
          onClose={closeTemplateDetails}
          isSpanish={isSpanish}
          formatFieldName={formatFieldName}
          organizeTemplateFields={organizeTemplateFields}
          openInEditMode={openInEditMode}
          onEditModeChange={(isEdit) => setOpenInEditMode(false)} // Reset flag after modal handles it
          onOpenFieldSelector={(template) => {
            setFieldSelectorTemplate(template);
            setSelectedTemplateForDetails(null);
            // Don't reopen main template modal when opening field selector
            setShouldShowMainModal(false);
          }}
        />
      )}

      {/* Field Selector Modal - Renders independently */}
      {fieldSelectorTemplate && (
        <FieldSelectorModal
          isOpen={!!fieldSelectorTemplate}
          template={fieldSelectorTemplate}
          onClose={(fieldWasSelected = false, updatedTemplateFromSelector = null) => {
            // Use the updated template passed from selector, or get from Redux, or fallback to original
            const updatedTemplate = updatedTemplateFromSelector || 
              reduxTemplates.find(t => t.id === fieldSelectorTemplate.id) || 
              fieldSelectorTemplate;
            // Reopen the template details modal when closing field selector
            setSelectedTemplateForDetails(updatedTemplate);
            setFieldSelectorTemplate(null);
            // If a field was selected, open in edit mode
            if (fieldWasSelected) {
              setOpenInEditMode(true);
            }
          }}
          isSpanish={isSpanish}
        />
      )}
    </>
  );
};

// Field definitions for search/selection
const getAvailableFields = (isSpanish) => {
  return {
    declaration: [
      { name: 'tipo', label: !isSpanish ? 'Type' : 'Tipo' },
      { name: 'fecha_registro', label: !isSpanish ? 'Registration Date' : 'Fecha Registro' },
      { name: 'fecha_creacion', label: !isSpanish ? 'Creation Date' : 'Fecha Creación' },
      { name: 'cliente_id', label: !isSpanish ? 'Client ID' : 'Cliente ID' },
      { name: 'agente_id', label: !isSpanish ? 'Agent ID' : 'Agente ID' },
      { name: 'remitente_id', label: !isSpanish ? 'Sender ID' : 'Remitente ID' },
      { name: 'declaracion_1', label: !isSpanish ? 'Declaration 1' : 'Declaración 1' },
      { name: 'declaracion_2', label: !isSpanish ? 'Declaration 2' : 'Declaración 2' },
      { name: 'declaracion_3', label: !isSpanish ? 'Declaration 3' : 'Declaración 3' },
      { name: 'aduana_entrada', label: !isSpanish ? 'Entry Customs' : 'Aduana Entrada' },
      { name: 'aduana_salida', label: !isSpanish ? 'Exit Customs' : 'Aduana Salida' },
      { name: 'aduana_despacho', label: !isSpanish ? 'Clearance Customs' : 'Aduana Despacho' },
      { name: 'pais_destino', label: !isSpanish ? 'Destination Country' : 'País Destino' },
      { name: 'pais_exportacion', label: !isSpanish ? 'Export Country' : 'País Exportación' },
      { name: 'pais_transporte_frontera', label: !isSpanish ? 'Border Transport Country' : 'País Transporte Frontera' },
      { name: 'modo_transporte_frontera', label: !isSpanish ? 'Border Transport Mode' : 'Modo Transporte Frontera' },
      { name: 'modo_transporte_interior', label: !isSpanish ? 'Internal Transport Mode' : 'Modo Transporte Interior' },
      { name: 'identidad_transporte_frontera', label: !isSpanish ? 'Border Transport Identity' : 'Identidad Transporte Frontera' },
      { name: 'factura', label: !isSpanish ? 'Invoice' : 'Factura' },
      { name: 'fecha_factura', label: !isSpanish ? 'Invoice Date' : 'Fecha Factura' },
      { name: 'condiciones_entrega', label: !isSpanish ? 'Delivery Terms' : 'Condiciones Entrega' },
      { name: 'naturaleza_transaccion', label: !isSpanish ? 'Transaction Nature' : 'Naturaleza Transacción' },
      { name: 'observaciones', label: !isSpanish ? 'Observations' : 'Observaciones' },
      { name: 'tiene_contenedores', label: !isSpanish ? 'Has Containers' : 'Tiene Contenedores' },
      { name: 'total_bultos', label: !isSpanish ? 'Total Packages' : 'Total Bultos' },
      { name: 'estado_edi', label: !isSpanish ? 'EDI Status' : 'Estado EDI' },
      { name: 'estado_edi_canarias', label: !isSpanish ? 'Canary Islands EDI Status' : 'Estado EDI Canarias' },
      { name: 'estado_pdc', label: !isSpanish ? 'PDC Status' : 'Estado PDC' },
      { name: 'autorizacion_por_operacion', label: !isSpanish ? 'Authorization per Operation' : 'Autorización por Operación' },
      { name: 'es_simplificado', label: !isSpanish ? 'Simplified' : 'Simplificado' },
      { name: 'es_recapitulativo', label: !isSpanish ? 'Recapitulative' : 'Recapitulativo' },
      { name: 'completo', label: !isSpanish ? 'Complete' : 'Completo' },
      { name: 'es_plantilla', label: !isSpanish ? 'Is Template' : '¿Es Plantilla?' },
      { name: 'e_mail', label: !isSpanish ? 'Contact Email' : 'Correo Electrónico de Contacto' },
      { name: 'contacto_telefono', label: !isSpanish ? 'Contact Phone' : 'Teléfono de Contacto' },
      { name: 'pais_ubicacion', label: !isSpanish ? 'Location Country' : 'País Ubicación' },
      { name: 'tipo_ubicacion', label: !isSpanish ? 'Location Type' : 'Tipo Ubicación' },
      { name: 'localizacion', label: !isSpanish ? 'Location Code' : 'Código de Localización' },
      { name: 'divisa', label: !isSpanish ? 'Currency Code' : 'Código Divisa' },
      { name: 'cambio_divisa', label: !isSpanish ? 'Exchange Rate' : 'Cambio Divisa' },
      { name: 'divisa_iso', label: !isSpanish ? 'Currency ISO' : 'Divisa ISO' },
      { name: 'regimen_solicitud', label: !isSpanish ? 'Request Regime' : 'Régimen Solicitud' },
      { name: 'telefono', label: !isSpanish ? 'Phone' : 'Teléfono' },
      { name: 'contacto', label: !isSpanish ? 'Contact' : 'Contacto' },
      { name: 'marcas', label: !isSpanish ? 'Marks' : 'Marcas' },
      { name: 'medio_transporte', label: !isSpanish ? 'Transport Mode' : 'Medio Transporte' },
      { name: 'provincia', label: !isSpanish ? 'Province' : 'Provincia' },
      { name: 'localizacion_mercancia', label: !isSpanish ? 'Goods Location' : 'Localización Mercancía' },
    ],
    document: [
      { name: 'tipo', label: !isSpanish ? 'Type' : 'Tipo' },
      { name: 'codigo_documento', label: !isSpanish ? 'Document Code' : 'Código de Documento' },
      { name: 'numero', label: !isSpanish ? 'Number' : 'Número' },
      { name: 'fecha', label: !isSpanish ? 'Date' : 'Fecha' },
      { name: 'descripcion', label: !isSpanish ? 'Description' : 'Descripción' },
    ],
  };
};

// Template Details Modal Component
const TemplateDetailsModal = ({
  template,
  isOpen,
  onClose,
  isSpanish,
  formatFieldName,
  organizeTemplateFields,
  onOpenFieldSelector,
  openInEditMode = false,
  onEditModeChange,
}) => {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState(template);

  // Handle opening in edit mode
  useEffect(() => {
    if (openInEditMode) {
      setIsEditMode(true);
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    }
  }, [openInEditMode, onEditModeChange]);

  useEffect(() => {
    setEditedTemplate(template);
    // Reset edit mode flag if template changes (e.g., when reopening)
    if (openInEditMode && onEditModeChange) {
      // Reset the flag after handling it
      setTimeout(() => {
        if (onEditModeChange) {
          onEditModeChange(false);
        }
      }, 0);
    }
  }, [template, openInEditMode, onEditModeChange]);

  if (!isOpen) return null;

  const { declarationFields, documentFields } = organizeTemplateFields(editedTemplate);

  const handleFieldChange = (fieldName, value, isDocument = false, docIndex = null) => {
    const newData = { ...editedTemplate.data };
    
    if (isDocument && docIndex !== null) {
      if (!newData.documentos) {
        newData.documentos = [];
      }
      const newDocumentos = [...newData.documentos];
      if (!newDocumentos[docIndex]) {
        newDocumentos[docIndex] = {};
      }
      newDocumentos[docIndex] = { ...newDocumentos[docIndex], [fieldName]: value };
      newData.documentos = newDocumentos;
    } else {
      newData[fieldName] = value;
    }
    
    setEditedTemplate({ ...editedTemplate, data: newData });
  };

  const handleRemoveField = (fieldName, isDocument = false, docIndex = null) => {
    const newData = { ...editedTemplate.data };
    
    if (isDocument && docIndex !== null) {
      if (newData.documentos && newData.documentos[docIndex]) {
        const newDocumentos = [...newData.documentos];
        const newDoc = { ...newDocumentos[docIndex] };
        delete newDoc[fieldName];
        if (Object.keys(newDoc).length === 0) {
          newDocumentos.splice(docIndex, 1);
        } else {
          newDocumentos[docIndex] = newDoc;
        }
        newData.documentos = newDocumentos.length > 0 ? newDocumentos : undefined;
      }
    } else {
      delete newData[fieldName];
    }
    
    setEditedTemplate({ ...editedTemplate, data: newData });
  };

  const handleSave = () => {
    dispatch(updateTemplate(editedTemplate));
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setEditedTemplate(template);
    setIsEditMode(false);
  };

  // Render field card component
  const renderFieldCard = (field, isDocument = false, docIndex = null) => {
    const hasValue = field.value !== null && field.value !== undefined && field.value !== '';
    let bgColor = '';
    if (!hasValue) {
      bgColor = 'bg-red-50';
    } else {
      bgColor = 'bg-gray-100';
    }
    
    return (
      <div 
        key={field.name}
        className={`text-sm break-words ${bgColor}a py-1 rounded-full relative`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 flex items-end gap-2">
            <span className={`font-semibold break-words text-gray-700`}>
              {formatFieldName(field.name)}
            </span>
            <div className="text-[10px] mb-[-2px] text-gray-500 font-mono break-all">
              {field.name}
            </div>
          </div>
          {isEditMode && (
            <button
              onClick={() => handleRemoveField(field.name, isDocument, docIndex)}
              className="text-red-500 hover:text-red-700 text-xs pl-2"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {isEditMode ? (
          <input
            type="text"
            value={hasValue ? String(field.value) : ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value, isDocument, docIndex)}
            className="text-xs mt-1 w-full py-1 border border-gray-300 rounded"
            placeholder={isSpanish ? 'Ingrese valor' : 'Enter value'}
          />
        ) : (
          <div className={`text-xs mt-1 break-words ${hasValue ? 'text-gray-600' : 'text-red-500'}`}>
            {hasValue 
              ? (typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value))
              : (isSpanish ? 'Sin valor' : 'No value')
            }
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-999">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-5 h-5 rounded-full flex-shrink-0"
              style={{ backgroundColor: editedTemplate.color }}
            />
            <h2 className="text-xl font-bold uppercase">{editedTemplate.title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 text-default rounded-full"
              >
                <Edit2 className="w-4 h-4" />
                {isSpanish ? 'Editar' : 'Edit'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-default text-white rounded-full"
                >
                  <Save className="w-4 h-4 stroke-white" />
                  {isSpanish ? 'Guardar' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm bg-gray-100 text-default rounded-full"
                >
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/* Declaration Fields */}
            {declarationFields.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base text-gray-700">
                    {!isSpanish ? "Declaration Fields" : "Campos de Declaración"}
                  </h3>
                </div>
                {isEditMode && (
          <div className="!mb-4 pt-2 flex gap-2">
            <button
              onClick={() => {
                // Save current edits before opening field selector
                dispatch(updateTemplate(editedTemplate));
                onOpenFieldSelector(editedTemplate);
              }}
              className="flex items-center gap-1 px-4 py-2 text-sm bg-secondary text-default rounded-full"
            >
              <Plus className="w-4 h-4" />
              {isSpanish ? 'Agregar Campo' : 'Add Field'}
            </button>
          </div>
        )}
                <div className="space-y-2">
                  {declarationFields.map((field) => renderFieldCard(field, false, null))}
                </div>
              </div>
            )}
            
            {/* Document Fields */}
            {documentFields.length > 0 && (
              <div className="ml-4 space-y-2">
                <h4 className="font-medium text-sm mb-2 text-gray-600">
                  {!isSpanish ? "Documents" : "Documentos"}
                </h4>
                {documentFields.map((doc, docIdx) => (
                  <div key={docIdx} className="ml-4 mb-2">
                    <p className="text-xs font-medium mb-1 text-gray-500">
                      {!isSpanish ? "Document" : "Documento"} #{doc.docIndex + 1}
                    </p>
                    <div className="space-y-2">
                      {doc.fields.map((field) => renderFieldCard(field, true, doc.docIndex))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {declarationFields.length === 0 && documentFields.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">
                {isSpanish ? 'No hay campos disponibles' : 'No fields available'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Field Selector Modal Component
const FieldSelectorModal = ({
  isOpen,
  onClose,
  template,
  isSpanish,
}) => {
  const dispatch = useDispatch();
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [selectedFieldLevel, setSelectedFieldLevel] = useState('declaration');

  if (!isOpen || !template) return null;

  // Get existing field names from template
  const getExistingFields = () => {
    const existingDeclarationFields = new Set();
    const existingDocumentFields = new Set();
    
    const { data } = template;
    
    // Get declaration fields (all keys except 'documentos')
    Object.keys(data).forEach((key) => {
      if (key !== 'documentos' && data[key] !== null && data[key] !== undefined) {
        existingDeclarationFields.add(key);
      }
    });
    
    // Get document fields (from all documentos)
    if (data.documentos && Array.isArray(data.documentos)) {
      data.documentos.forEach((doc) => {
        if (doc && typeof doc === 'object') {
          Object.keys(doc).forEach((key) => {
            if (doc[key] !== null && doc[key] !== undefined) {
              existingDocumentFields.add(key);
            }
          });
        }
      });
    }
    
    return { existingDeclarationFields, existingDocumentFields };
  };

  const { existingDeclarationFields, existingDocumentFields } = getExistingFields();
  
  const availableFields = getAvailableFields(isSpanish);
  const filteredFields = availableFields[selectedFieldLevel]?.filter(field => {
    // Filter by search query
    const matchesSearch = field.name.toLowerCase().includes(fieldSearchQuery.toLowerCase()) ||
      field.label.toLowerCase().includes(fieldSearchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Filter out existing fields
    if (selectedFieldLevel === 'declaration') {
      return !existingDeclarationFields.has(field.name);
    } else if (selectedFieldLevel === 'document') {
      return !existingDocumentFields.has(field.name);
    }
    
    return true;
  }) || [];

  const handleSelectField = (fieldName, fieldLevel) => {
    const newData = { ...template.data };
    
    if (fieldLevel === 'document') {
      if (!newData.documentos) {
        newData.documentos = [];
      }
      newData.documentos.push({ [fieldName]: '' });
    } else {
      newData[fieldName] = '';
    }
    
    const updatedTemplate = { ...template, data: newData };
    dispatch(updateTemplate(updatedTemplate));
    
    // Close with flag and pass the updated template directly
    onClose(true, updatedTemplate);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-999">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {isSpanish ? 'Seleccionar Campo' : 'Select Field'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSelectedFieldLevel('declaration')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'declaration'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Declaración' : 'Declaration'}
          </button>
          <button
            onClick={() => setSelectedFieldLevel('document')}
            className={`flex-1 px-4 py-2 text-sm rounded-full font-medium ${
              selectedFieldLevel === 'document'
                ? 'bg-default text-white rounded-full'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isSpanish ? 'Documento' : 'Document'}
          </button>
        </div>

        <div className="relative mb-4 border-container rounded-full px-3 py-1 flex-1 flex-row">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={fieldSearchQuery}
            onChange={(e) => setFieldSearchQuery(e.target.value)}
            placeholder={isSpanish ? 'Buscar campo...' : 'Search field...'}
            className="w-full pl-7 pr-4 py-2 !border-none no-border search-input text-sm focus:outline-none"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2">
            {filteredFields.length > 0 ? (
              filteredFields.map((field) => (
                <button
                  key={field.name}
                  onClick={() => handleSelectField(field.name, selectedFieldLevel)}
                  className="w-full text-left px-5 py-4 bg-gray-100 rounded-full hover-scale-none  flex justify-between items-center transition-scale"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-900">{field.label}</span>
                    <span className="text-[8px] text-gray-500 font-mono mt-1">{field.name}</span>
                  </div>
                  <Plus className="w-4 h-4 text-secondary flex-shrink-0" />
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">
                  {isSpanish ? 'No se encontraron campos' : 'No fields found'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

